import { useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import api from '../lib/api';
import { useAuthStore } from '../store/authStore';
import { useSessionStore } from '../store/sessionStore';
import { validateRcdResult, PUSH_BUTTON_FAILURE_DEFECT, DID_NOT_TRIP_DEFECT } from '../lib/testValidators';
import { getDischargeStartTimeIso } from '../lib/sessionTimerUtils';

/**
 * Flatten the results queue and the parallel secondary-test queue into a single
 * /tests/batch payload. A single asset can contribute up to two rows — its
 * primary result (from `results`, e.g. an RCD's Operating Time or a dual-check
 * asset's Visual Check) and a secondary test (from `secondaryTestQueue`, e.g. a
 * Push Button or Operational Check) — which the backend merges into
 * next_due_schedule within one transaction. Entries already carrying a server
 * testId are skipped.
 */
function buildBatchPayload(latest) {
  const toRow = (item, assetId) => {
    if (!item || item.testId != null) return null;
    return { ...item, assetId, sessionId: latest.sessionId };
  };
  const fromResults = latest.queue.map((assetId) => toRow(latest.results[assetId], assetId));
  const fromSecondary = latest.secondaryTestQueueIds.map(
    (assetId) => toRow(latest.secondaryTestQueue[assetId], assetId)
  );
  return [...fromResults, ...fromSecondary].filter(Boolean);
}

/**
 * Build the POST /sessions body from session-store state. `workOrderNumber` is
 * sent whenever present, on any session type — a repair session's own work
 * order box already covers the repair case, and an inspection can be tied to
 * a work order too (Brief 18). Empty/whitespace values are omitted entirely,
 * not sent as `''`, so an unset work order prints as a dash rather than blank.
 */
export function buildCreateSessionBody({ sessionId, siteId, sessionElementType, sessionType, workOrderNumber }) {
  const body = {
    id: String(sessionId).trim(),
    siteId: Number(siteId),
    startTime: new Date().toISOString(),
  };
  if (sessionElementType) {
    body.elementType = sessionElementType;
  }
  const st =
    sessionType && ['inspection', 'repair'].includes(String(sessionType).trim().toLowerCase())
      ? String(sessionType).trim().toLowerCase()
      : 'inspection';
  body.sessionType = st;
  if (workOrderNumber != null && String(workOrderNumber).trim() !== '') {
    body.workOrderNumber = String(workOrderNumber).trim();
  }
  return body;
}

export function useTestSession(options = {}) {
  const { resumeSessionId } = options;
  const queryClient = useQueryClient();
  const store = useSessionStore();
  const markSessionCreated = store.markSessionCreated;
  const markSynced = store.markSynced;
  const markSecondaryTestSynced = store.markSecondaryTestSynced;
  const createSessionInFlight = useRef(false);

  // --- Session lifecycle: create session on server when we have sessionId + siteId + pending ---
  const { mutate: createSession } = useMutation(
    async () => {
      const { sessionId, siteId, sessionElementType, sessionType, workOrderNumber } = useSessionStore.getState();
      const siteIdNum = Number(siteId);
      if (!sessionId || String(sessionId).trim() === '') {
        throw new Error('Session ID is missing');
      }
      if (!Number.isInteger(siteIdNum) || siteIdNum < 1) {
        throw new Error('Please select a site first');
      }
      const body = buildCreateSessionBody({ sessionId, siteId, sessionElementType, sessionType, workOrderNumber });
      try {
        const { data } = await api.post('/sessions', body);
        return data;
      } catch (err) {
        if (err.response?.status === 409) {
          const existingSessionId = err.response?.data?.existingSessionId;
          if (existingSessionId != null && String(existingSessionId).trim() !== '') {
            return { adoptExistingId: String(existingSessionId).trim() };
          }
          return { id: sessionId };
        }
        throw err;
      }
    },
    {
      onSuccess: (data) => {
        createSessionInFlight.current = false;
        if (data?.adoptExistingId) {
          const existingId = String(data.adoptExistingId).trim();
          const state = useSessionStore.getState();
          const siteId = state.siteId;
          const sessionElementType = state.sessionElementType;
          const sessionType = state.sessionType;
          const workOrderNumber = state.workOrderNumber;
          state.resetSession();
          state.initSession(siteId, existingId, { sessionType });
          state.setSessionElementType(sessionElementType);
          state.setWorkOrderNumber(workOrderNumber);
          toast.success('Resuming your existing in-progress session for this site.');
        }
        markSessionCreated();
        queryClient.invalidateQueries(['sessions-in-progress']);
      },
      onError: (err) => {
        createSessionInFlight.current = false;
        console.error('Create session failed', err);
        const msg = err.response?.data?.error || err.response?.data?.message || err.message;
        const details = err.response?.data?.details;
        toast.error(details ? `${msg}: ${details}` : msg || 'Could not track session start');
      },
    }
  );

  useEffect(() => {
    const { sessionId, siteId, serverSessionStatus } = useSessionStore.getState();
    const siteIdNum = Number(siteId);

    if (resumeSessionId && String(sessionId) === String(resumeSessionId)) {
      if (serverSessionStatus === 'pending') {
        store.markSessionCreated();
      }
      return;
    }

    const valid =
      sessionId &&
      String(sessionId).trim() !== '' &&
      Number.isInteger(siteIdNum) &&
      siteIdNum >= 1 &&
      serverSessionStatus === 'pending' &&
      !createSessionInFlight.current;
    if (valid) {
      createSessionInFlight.current = true;
      createSession();
    }
  }, [store.sessionId, store.siteId, store.serverSessionStatus, createSession, resumeSessionId, markSessionCreated]);

  // --- Cancel session: DELETE /sessions/:id then reset locally ---
  const { mutateAsync: cancelSessionOnServer, isLoading: isCancelling } = useMutation(
    async () => {
      const { sessionId } = useSessionStore.getState();
      await api.delete(`/sessions/${sessionId}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['sessions-recent']);
        queryClient.invalidateQueries(['sessions-in-progress']);
      },
      onError: (err) => {
        console.error('Cancel session failed', err);
      },
    }
  );

  const refreshSessionLists = async () => {
    await Promise.all([
      queryClient.refetchQueries(['sessions-recent']),
      queryClient.refetchQueries(['sessions-in-progress']),
    ]);
  };

  const dropSessionFromInProgressCache = (sessionId) => {
    if (!sessionId) return;
    const user = useAuthStore.getState().user;
    const userId = user?.id ?? user?.userId;
    // Key must match the user-scoped fetchers in Dashboard / useSites / useSiteDetails.
    queryClient.setQueryData(['sessions-in-progress', userId], (old) => {
      if (!Array.isArray(old)) return [];
      return old.filter((s) => String(s.id) !== String(sessionId));
    });
  };

  const cancelSession = async () => {
    const { sessionId, serverSessionStatus } = useSessionStore.getState();
    if (!sessionId || serverSessionStatus !== 'created') {
      store.resetSession();
      await refreshSessionLists();
      return;
    }
    const sid = sessionId;
    try {
      await cancelSessionOnServer();
    } catch (err) {
      // 404 = session never made it to DB (stale localStorage) — safe to clear locally.
      // Any other error is already logged by onError; still reset so user isn't stuck.
    }
    store.resetSession();
    dropSessionFromInProgressCache(sid);
    await refreshSessionLists();
  };

  // --- Finish session: PUT /end then reset locally ---
  const {
    mutateAsync: endSessionOnServer,
    isLoading: isFinishing,
  } = useMutation(
    async () => {
      const { sessionId } = useSessionStore.getState();
      await api.put(`/sessions/${sessionId}/end`, {
        endTime: new Date().toISOString(),
      });
    },
    {
      onError: (err) => {
        console.error('End session failed', err);
      }
    }
  );

  const finishSession = async () => {
    if (!store.sessionId) {
      store.resetSession();
      return;
    }
    const sid = store.sessionId;
    // Results are sent 5s after the last tap, so Finish can arrive first. Send
    // anything still unsent before ending, or resetSession() would delete it.
    const pending = buildBatchPayload(useSessionStore.getState());
    if (pending.length > 0) {
      try {
        await syncBatchAsync(pending);
      } catch (err) {
        const n = pending.length;
        toast.error(`${n} result${n === 1 ? '' : 's'} couldn't be sent. Check your signal and tap Finish again.`);
        throw err;
      }
    }
    await endSessionOnServer();
    store.resetSession();
    dropSessionFromInProgressCache(sid);
    await refreshSessionLists();
  };

  // --- Batch Sync Logic ---

  const { mutate: syncBatch, mutateAsync: syncBatchAsync, isLoading: isSyncing } = useMutation(
    async (payload) => {
      const { data } = await api.post('/tests/batch', { tests: payload });
      return data;
    },
    {
      onSuccess: (data) => {
        if (Array.isArray(data)) {
          let syncedCount = 0;
          data.forEach((test) => {
            const assetId = test.asset_id ?? test.assetId;
            const testId = test.id;
            if (assetId == null || testId == null) return;
            const testType = test.test_type ?? test.testType;
            const state = useSessionStore.getState();

            // Secondary-test rows (Push Button, Operational Check, …) belong to
            // the parallel queue. Gate on a pending secondaryTestQueue entry whose
            // testType matches, so the primary result row is never misrouted and a
            // late/duplicate response can't create a phantom entry.
            const secondary = state.secondaryTestQueue[assetId];
            if (secondary && secondary.testType === testType) {
              const canMarkSecondary =
                secondary.synced !== true ||
                secondary.testId == null ||
                String(secondary.testId) === String(testId);
              if (canMarkSecondary) {
                store.markSecondaryTestSynced(assetId, testId);
                syncedCount++;
              }
              return;
            }

            // Mark as synced when entry is unsynced, while guarding against
            // late duplicate responses overwriting an already-synced server ID.
            const current = state.results[assetId];
            const canMarkSynced =
              !!current &&
              (
                current.synced !== true ||
                current.testId == null ||
                String(current.testId) === String(testId)
              );
            if (canMarkSynced) {
              store.markSynced(assetId, testId);
              syncedCount++;
            }
          });
          if (syncedCount > 0) {
            queryClient.invalidateQueries(['assets']);
          }
        }
      },
      onError: (err) => {
        console.error('Batch sync failed', err);
      }
    }
  );

  // --- Auto-Sync Watcher (debounced 5s) ---
  useEffect(() => {
    const stateNow = useSessionStore.getState();

    // Drain orphaned ids (an id with no matching entry, e.g. from a corrupt
    // persisted state) so a broken entry can't wedge the sync loop.
    if (stateNow.queue.length > 0) {
      const orphans = stateNow.queue.filter((assetId) => !stateNow.results[assetId]);
      if (orphans.length > 0) {
        orphans.forEach((assetId) => markSynced(assetId, null));
        return;
      }
    }
    if (stateNow.secondaryTestQueueIds.length > 0) {
      const secOrphans = stateNow.secondaryTestQueueIds.filter((assetId) => !stateNow.secondaryTestQueue[assetId]);
      if (secOrphans.length > 0) {
        secOrphans.forEach((assetId) => markSecondaryTestSynced(assetId, null));
        return;
      }
    }

    if (stateNow.queue.length === 0 && stateNow.secondaryTestQueueIds.length === 0) return;

    const timer = setTimeout(() => {
      if (isSyncing) return;

      const payload = buildBatchPayload(useSessionStore.getState());
      if (payload.length > 0) {
        syncBatch(payload);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [store.queue, store.results, store.secondaryTestQueue, store.secondaryTestQueueIds, store.sessionId, isSyncing, syncBatch, markSynced, markSecondaryTestSynced]);

  const syncNow = () => {
    const latest = useSessionStore.getState();
    if (isSyncing) return;
    if (latest.queue.length === 0 && latest.secondaryTestQueueIds.length === 0) return;

    const payload = buildBatchPayload(latest);

    if (payload.length > 0) {
      toast.promise(syncBatchAsync(payload), {
        loading: 'Syncing...',
        success: 'Synced!',
        error: 'Sync failed'
      });
    }
  };

  // --- Public Actions ---

  const handleInlineRcdSave = (asset, testDate) => {
    // Read the freshest inputs directly from the store (not the render snapshot)
    // so a debounced auto-save (see TestSessionView.handleRcdInputChange) always
    // acts on the value the tech last entered, never a stale keystroke.
    const input = useSessionStore.getState().rcdInputs[asset.id] ?? { tripTimeMs: '' };

    // Never default to Pass. The push-button check is its own required result,
    // so refuse to save until the tech has explicitly answered it — do not lean
    // on the trip-time validation below as an accidental safety net.
    if (input.pushButton !== 'pass' && input.pushButton !== 'fail') {
      toast.error('Push button result required before saving');
      return;
    }

    // Mirrors buildRecordTestPayload's RCD branch (Brief 37/38): the reason
    // dropdown is a selected value, not a checkbox. `Did Not Trip` is one of
    // its options, detected by string equality — there is no separate flag.
    const pushPass = input.pushButton === 'pass';
    const selectedReason = (input.reason || '').trim();
    const didNotTrip = selectedReason === DID_NOT_TRIP_DEFECT;

    let tripTime = null;
    let rcdValidation = null;

    // Validate Trip Time ONLY when the push button passed and the RCD actually
    // tripped — a "did not trip" record carries no measurement, same guard as
    // the modal.
    if (pushPass && !didNotTrip) {
      rcdValidation = validateRcdResult(input.tripTimeMs, 'pass');
      if (!rcdValidation.valid) {
        toast.error(rcdValidation.error || 'Please enter a valid Trip Time (ms)');
        return;
      }
      tripTime = rcdValidation.tripTimeMs;
    }

    // Pass requires: push button passed, a trip time was measured within the
    // limit, AND no failure reason is selected — selecting any reason (even on
    // an otherwise-passing RCD, e.g. "Poor Condition") forces Fail, same as the
    // modal.
    const measuredPass = pushPass && !didNotTrip && rcdValidation?.result === 'pass';
    const result = measuredPass && !selectedReason ? 'pass' : 'fail';

    const payload = {
      testDate: testDate ?? new Date().toISOString().split('T')[0],
      testType: 'Operating Time',
      result,
      metrics: tripTime !== null ? { trip_time_ms: tripTime } : null,
    };

    if (result === 'fail') {
      // Derived reasons first, then the selected reason — mirrors
      // buildRecordTestPayload's join order exactly. Do not invent a second
      // join order here.
      const parts = [];
      if (!pushPass) parts.push(PUSH_BUTTON_FAILURE_DEFECT);
      if (pushPass && !didNotTrip && rcdValidation?.autoDefect) parts.push(rcdValidation.autoDefect);
      if (selectedReason) parts.push(selectedReason);
      if (parts.length) payload.defectsFound = parts.join(', ');
    }

    store.stageResult(asset.id, payload);

    // Stage the Push Button test as a SEPARATE parallel (secondary) event. Without
    // this, the push_button clock in next_due_schedule never advances (only
    // Operating Time is ever sent), so every RCD tested through Quick-pass silently
    // goes overdue on its push-button interval ~12 months after seeding. Reuses
    // input.pushButton.
    store.stageSecondaryTest(asset.id, {
      testDate: payload.testDate,
      testType: 'Push Button',
      result: pushPass ? 'pass' : 'fail',
      defectsFound: !pushPass ? PUSH_BUTTON_FAILURE_DEFECT : undefined,
    });

    if (result === 'fail') toast('Recorded Fail', { icon: '⚠️', duration: 2000 });
    else toast.success('Recorded Pass');
  };

  const handleQuickPass = (asset, testType, testDate) => {
    const payload = { testDate, testType, result: 'pass' };

    // Exit/emergency lights only (90-minute Discharge). RCD and fan/heater
    // quick-passes have no discharge timer to stamp. Shares the exact "which
    // timer applies to this asset" lookup RecordTestModal uses, so a
    // quick-pass captures the same start time a modal save would (Brief 18 §3).
    if (testType === '90-minute Discharge') {
      const { groupTimers, siteId } = useSessionStore.getState();
      const startedAtIso = getDischargeStartTimeIso(asset, groupTimers, siteId);
      if (startedAtIso != null) {
        payload.testStartedAt = startedAtIso;
      }
    }

    store.stageResult(asset.id, payload);
  };

  const registerResult = (assetId, { result, testId, tripTimeMs }) => {
    store.setResultFromServer(assetId, result, testId, tripTimeMs);
    queryClient.invalidateQueries(['assets']);
  };

  return {
    ...store,
    handleInlineRcdSave,
    handleQuickPass,
    registerResult,
    isSyncing,
    syncNow,
    finishSession,
    isFinishing,
    cancelSession,
    isCancelling,
  };
}